import React, { useState, useEffect } from "react";
import "../assets/styles/ProductReviews.css";
import { ReviewBaseUrl } from "../config/api";

const ProductReviews = ({ productId, productName }) => {
  const [sortBy, setSortBy] = useState("latest");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [ratingDistribution, setRatingDistribution] = useState([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state for new review
  const [newReview, setNewReview] = useState({
    userName: '',
    userEmail: '',
    rating: 0,
    title: '',
    content: '',
    wouldRecommend: false
  });
  const [submitting, setSubmitting] = useState(false);

  const buildCandidateUrls = (path) => {
    const base = (ReviewBaseUrl || "").replace(/\/$/, "");
    const cleanPath = (path || "").replace(/^\//, "");
    return [`${base}/${cleanPath}`, `${base}/api/${cleanPath}`];
  };

  const readResponseBody = async (response) => {
    const contentType = response.headers?.get?.("content-type") || "";
    if (contentType.includes("application/json")) {
      return response.json();
    }
    const text = await response.text();
    return { _nonJson: true, text };
  };

  const fetchFirstOk = async (path, options) => {
    let lastError;
    for (const url of buildCandidateUrls(path)) {
      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          const body = await readResponseBody(response);
          lastError = new Error(
            body && body._nonJson
              ? `Request failed (${response.status})`
              : body?.error || body?.message || `Request failed (${response.status})`
          );
          continue;
        }
        const body = await readResponseBody(response);
        if (body && body._nonJson) {
          lastError = new Error("Unexpected non-JSON response");
          continue;
        }
        return body;
      } catch (e) {
        lastError = e;
      }
    }
    throw lastError || new Error("Request failed");
  };

  // Fetch reviews from backend
  const fetchReviews = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        sortBy,
        rating: ratingFilter,
        search: searchQuery
      });

      const data = await fetchFirstOk(`reviews/product/${productId}?${params}`);
      setReviews(data.reviews);
      setRatingDistribution(data.ratingDistribution);
      setTotalReviews(data.totalReviews);
      setAverageRating(data.averageRating);
      setError(null);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Failed to load reviews');
      // Set fallback data to ensure UI works
      setReviews([]);
      setRatingDistribution([5, 4, 3, 2, 1].map(star => ({ star, count: 0, percentage: 0 })));
      setTotalReviews(0);
      setAverageRating(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchReviews();
    }
  }, [productId, sortBy, ratingFilter, searchQuery]);

  // Calculate average rating (fallback for UI)
  const displayAverageRating = averageRating || (reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(2)
    : 0);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span key={i} className={`star ${i < rating ? 'filled' : ''}`}>★</span>
    ));
  };

  // Submit new review
  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (!newReview.userName || !newReview.userEmail || !newReview.rating || !newReview.title || !newReview.content) {
      alert('Please fill in all required fields');
      return;
    }

    if (newReview.rating < 1 || newReview.rating > 5) {
      alert('Please select a rating');
      return;
    }

    try {
      setSubmitting(true);
      const result = await fetchFirstOk(`reviews/product/${productId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newReview)
      });

      // Add new review to the list
      const nextReviews = [...reviews, result.review];
      setReviews(nextReviews);

      const nextTotal = nextReviews.length;
      const nextAverage = nextTotal > 0
        ? (nextReviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) / nextTotal).toFixed(2)
        : 0;
      setTotalReviews(nextTotal);
      setAverageRating(nextAverage);

      const nextDistribution = [5, 4, 3, 2, 1].map((star) => {
        const count = nextReviews.filter((r) => Number(r.rating) === star).length;
        return {
          star,
          count,
          percentage: nextTotal > 0 ? (count / nextTotal) * 100 : 0,
        };
      });
      setRatingDistribution(nextDistribution);

      // Reset form
      setNewReview({
        userName: '',
        userEmail: '',
        rating: 0,
        title: '',
        content: '',
        wouldRecommend: false
      });

      setShowWriteReview(false);
      alert('Review submitted successfully!');

    } catch (err) {
      console.error('Error submitting review:', err);
      alert(err.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle star rating selection
  const handleStarClick = (rating) => {
    setNewReview(prev => ({ ...prev, rating }));
  };

  const filteredReviews = reviews;

  return (
    <div className="product-reviews-section container">
      {loading && (
        <div className="loading-state">
          <p>Loading reviews...</p>
        </div>
      )}

      {!loading && (
        <>
          <div className="reviews-header">
            <h2 className="reviews-title">Worn & Adored.</h2>
          </div>

          <div className="reviews-summary">
            <div className="rating-overview">
              <div className="average-rating">
                <span className="rating-number">{displayAverageRating}</span>
                <div className="rating-stars">{renderStars(Math.round(displayAverageRating))}</div>
                <span className="review-count">{totalReviews} reviews</span>
              </div>
              <div className="rating-bars">
                {(ratingDistribution.length ? ratingDistribution : [5, 4, 3, 2, 1].map((star) => ({ star, percentage: 0 }))).map(({ star, percentage }) => (
                  <div key={star} className="rating-bar-row">
                    <span className="bar-label">{star}</span>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: `${percentage}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              className="write-review-btn"
              onClick={() => setShowWriteReview(true)}
            >
              WRITE A REVIEW
            </button>
          </div>

          <div className="reviews-filters">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <span className="search-icon">🔍</span>
            </div>

            <div className="filter-group">
              <label>Sort by:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="latest">Latest</option>
                <option value="highest">Highest Rating</option>
                <option value="lowest">Lowest Rating</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Rating</label>
              <select value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>

            <label className="photos-videos-toggle">
              <input type="checkbox" />
              <span>Photos & videos</span>
            </label>
          </div>

          {filteredReviews.length > 0 && (
            <div className="reviews-list">
              {filteredReviews.map((review) => (
                <div key={review._id || review.id} className="review-card">
                  <div className="review-user-info">
                    <div className="user-avatar">
                      <span className="avatar-icon">👤</span>
                      {review.isVerified && <span className="verified-badge">✓</span>}
                    </div>
                    <div className="user-details">
                      <span className="user-name">{review.userName}</span>
                      {review.isVerified && (
                        <span className="verified-text">
                          <span className="check-icon">✓</span> Verified buyer
                        </span>
                      )}
                      {review.isPurchaseVerified && (
                        <span className="purchase-verified">
                          <span className="check-icon">✓</span> Purchase verified by {review.verifiedBy}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="review-content">
                    <div className="review-header">
                      <div className="review-stars">{renderStars(review.rating)}</div>
                      <span className="review-date">{review.formattedDate || review.date}</span>
                    </div>
                    <h4 className="review-title">{review.title}</h4>
                    <p className="review-text">{review.content}</p>
                    <p className="review-product-info">{review.productInfo || `Review for ${productName}`}</p>

                    {review.wouldRecommend && (
                      <div className="would-recommend">
                        <span className="heart-icon">❤️</span> Would recommend
                      </div>
                    )}

                    <div className="review-helpful">
                      <span>Helpful</span>
                      <button className="helpful-btn up">△</button>
                      <button className="helpful-btn down">▽</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="see-more-container">
            <button className="see-more-btn">SEE MORE REVIEWS</button>
          </div>
        </>
      )}

      {/* Write Review Modal */}
      {showWriteReview && (
        <div className="review-modal-overlay" onClick={() => setShowWriteReview(false)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowWriteReview(false)}>×</button>
            <h3>Write a Review</h3>
            <form className="review-form" onSubmit={handleSubmitReview}>
              <div className="form-group">
                <label>Your Name</label>
                <input
                  type="text"
                  placeholder="Enter your name"
                  value={newReview.userName}
                  onChange={(e) => setNewReview(prev => ({ ...prev, userName: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Your Email</label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={newReview.userEmail}
                  onChange={(e) => setNewReview(prev => ({ ...prev, userEmail: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Your Rating</label>
                <div className="star-input">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`star-select ${star <= newReview.rating ? 'selected' : ''}`}
                      onClick={() => handleStarClick(star)}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>Review Title</label>
                <input
                  type="text"
                  placeholder="Give your review a title"
                  value={newReview.title}
                  onChange={(e) => setNewReview(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Your Review</label>
                <textarea
                  placeholder="Write your review here..."
                  rows="4"
                  value={newReview.content}
                  onChange={(e) => setNewReview(prev => ({ ...prev, content: e.target.value }))}
                  required
                ></textarea>
              </div>
              <div className="form-group checkbox">
                <input
                  type="checkbox"
                  id="recommend"
                  checked={newReview.wouldRecommend}
                  onChange={(e) => setNewReview(prev => ({ ...prev, wouldRecommend: e.target.checked }))}
                />
                <label htmlFor="recommend">I would recommend this product</label>
              </div>
              <button
                type="submit"
                className="submit-review-btn"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
